import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../auth/useApi';
import { Paperclip, Trash2, Upload } from 'lucide-react';

const C = {
  primary: 'var(--c-primary)', secondary: 'var(--c-secondary)', bg: 'var(--c-bg)',
  surface: 'var(--c-surface)', border: 'var(--c-border)', text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)', ok: 'var(--c-ok)', warn: 'var(--c-warn)', danger: 'var(--c-danger)',
};

interface TaskAttachment {
  AttachmentId: number;
  FileName: string;
  ContentType: string | null;
  SizeBytes: number;
  CreatedAt: string;
  UploadedByName: string | null;
  url: string | null;
  isImage: boolean;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const TaskAttachments: React.FC<{ taskId: number; canEdit: boolean }> = ({ taskId, canEdit }) => {
  const api = useApi();
  const [items, setItems] = useState<TaskAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const data = await api.get<{ attachments: TaskAttachment[] }>(`/api/projects/tasks/${taskId}/attachments`);
      setItems(Array.isArray(data.attachments) ? data.attachments : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const token = await api.getAccessToken();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${api.apiBase.replace(/\/$/, '')}/api/projects/tasks/${taskId}/attachments`, {
        method: 'POST',
        body: fd,
        headers: { Authorization: `Bearer ${token}` }, // no Content-Type — browser sets multipart boundary
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Upload ${res.status}: ${t || res.statusText}`);
      }
      await load();
    } catch (err: any) {
      alert(`Upload failed: ${err?.message ?? 'error'}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (attId: number) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await api.del(`/api/projects/tasks/${taskId}/attachments/${attId}`);
      await load();
    } catch (err: any) {
      alert(`Delete failed: ${err?.message ?? 'error'}`);
    }
  };

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Paperclip size={12} style={{ color: C.textMuted }} />
        <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.textMuted, fontWeight: 600 }}>
          Attachments
        </span>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: C.textMuted }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic', marginBottom: canEdit ? 8 : 0 }}>No attachments yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: canEdit ? 8 : 0 }}>
          {items.map(a => (
            <div key={a.AttachmentId} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              {a.isImage && a.url ? (
                <a href={a.url} target="_blank" rel="noreferrer">
                  <img
                    src={a.url}
                    alt={a.FileName}
                    style={{ maxWidth: 120, maxHeight: 120, borderRadius: 4, border: `1px solid ${C.border}`, display: 'block' }}
                  />
                </a>
              ) : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.text, wordBreak: 'break-word' }}>
                  {a.url ? (
                    <a href={a.url} target="_blank" rel="noreferrer" style={{ color: C.primary, textDecoration: 'none' }}>{a.FileName}</a>
                  ) : a.FileName}
                </div>
                <div style={{ fontSize: 11, color: C.textMuted }}>
                  {fmtSize(a.SizeBytes)}{a.UploadedByName ? ` · ${a.UploadedByName}` : ''}
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={() => remove(a.AttachmentId)}
                  title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.danger, display: 'inline-flex' }}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div>
          <input ref={fileRef} type="file" onChange={onPick} disabled={busy} style={{ display: 'none' }} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
          >
            <Upload size={12} /> {busy ? 'Uploading…' : 'Upload file'}
          </button>
        </div>
      )}
    </div>
  );
};
